import React from 'react';

interface SkeletonProps {
  className?: string;
}

/** A single shimmering placeholder block. Decorative — hidden from a11y tree. */
export const Skeleton: React.FC<SkeletonProps> = ({ className = '' }) => (
  <div className={`animate-pulse rounded-md bg-gray-200 dark:bg-slate-700 ${className}`} aria-hidden="true" />
);

interface SkeletonListProps {
  rows?: number;
  className?: string;
}

/** Placeholder rows for list/table pages while data loads. */
export const SkeletonList: React.FC<SkeletonListProps> = ({ rows = 5, className = '' }) => (
  <div className={`space-y-3 ${className}`} role="status" aria-busy="true">
    {Array.from({ length: rows }).map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-4 rounded-xl border border-gray-200 p-4 dark:border-slate-800"
      >
        <Skeleton className="h-10 w-10 rounded-xl" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    ))}
    <span className="sr-only">Loading…</span>
  </div>
);

interface SkeletonCardsProps {
  count?: number;
  className?: string;
}

/** Placeholder cards (e.g. stat tiles, company cards) while data loads. */
export const SkeletonCards: React.FC<SkeletonCardsProps> = ({ count = 4, className = '' }) => (
  <div className={`grid gap-4 ${className}`} role="status" aria-busy="true">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="space-y-3 rounded-xl border border-gray-200 p-5 dark:border-slate-800">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-8 w-1/3" />
      </div>
    ))}
    <span className="sr-only">Loading…</span>
  </div>
);

export default Skeleton;
